/**
 * Chronomètre de temps passé (RG-24, #44) : démarre à l’entrée dans le dossier, s’arrête après inactivité ou à la
 * sortie, reste corrigeable ; tout est comptabilisé, même ce qui n’est pas facturé. Tous les instants sont en
 * millisecondes ; le délai d’inactivité est une donnée versionnée passée par l’appelant. Chaque période close est enregistrée par
 * l’API (temps.enregistrer, RFC-RG24-001) ; la correction passe par temps.corriger, jamais par l’état de l’écran.
 */
export interface EtatChrono {
  ouvertA: number | null;
  derniereActivite: number | null;
  cumuleMs: number;
}

export const CHRONO_INITIAL: EtatChrono = { ouvertA: null, derniereActivite: null, cumuleMs: 0 };

export const ouvrir = (etat: EtatChrono, maintenant: number): EtatChrono =>
  etat.ouvertA === null ? { ...etat, ouvertA: maintenant, derniereActivite: maintenant } : etat;

/** Temps de la période en cours : jamais au-delà de la dernière activité plus le délai d’inactivité. */
const periodeEnCours = (etat: EtatChrono, maintenant: number, inactiviteMs: number): number =>
  etat.ouvertA === null ? 0 : Math.max(0, Math.min(maintenant, (etat.derniereActivite ?? etat.ouvertA) + inactiviteMs) - etat.ouvertA);

export const totalMs = (etat: EtatChrono, maintenant: number, inactiviteMs: number): number =>
  etat.cumuleMs + periodeEnCours(etat, maintenant, inactiviteMs);

export const fermer = (etat: EtatChrono, maintenant: number, inactiviteMs: number): EtatChrono =>
  etat.ouvertA === null ? etat : { ...etat, cumuleMs: totalMs(etat, maintenant, inactiviteMs), ouvertA: null, derniereActivite: null };

/** Une activité prolonge la période ; après une inactivité trop longue, la période s’arrête et une nouvelle commence. */
export function activite(etat: EtatChrono, maintenant: number, inactiviteMs: number): EtatChrono {
  if (etat.ouvertA === null) return etat;
  if (maintenant - (etat.derniereActivite ?? etat.ouvertA) > inactiviteMs)
    return ouvrir(fermer(etat, maintenant, inactiviteMs), maintenant);
  return { ...etat, derniereActivite: maintenant };
}

/**
 * Période en cours, bornée par l'inactivité : ce que le chronomètre envoie au serveur quand elle se clôt (sortie, inactivité,
 * fermeture de l'onglet, avant une correction). Nulle si le chronomètre est arrêté ou si la période est vide.
 */
export function periode(etat: EtatChrono, maintenant: number, inactiviteMs: number): { debut: number; fin: number } | null {
  if (etat.ouvertA === null) return null;
  const fin = etat.ouvertA + periodeEnCours(etat, maintenant, inactiviteMs);
  return fin > etat.ouvertA ? { debut: etat.ouvertA, fin } : null;
}

export function formaterDuree(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'moins d’une minute';
  const heures = Math.floor(minutes / 60);
  return heures > 0 ? `${heures} h ${String(minutes % 60).padStart(2, '0')} min` : `${minutes} min`;
}
